import { AfterContentInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy } from '@angular/core';

export function inlineActionCount(total: number): number { return total > 3 ? 2 : total; }

@Component({
  selector: 'app-list-actions',
  standalone: false,
  template: `
    <div class="list-action-bar">
      <ng-content select="[listPrimaryAction]"></ng-content>
      <button type="button" class="btn btn-outline-primary list-more-button"
        [hidden]="!hasOverflow"
        [attr.aria-expanded]="expanded" [attr.aria-controls]="menuId" (click)="toggle($event)">
        <i class="fas fa-ellipsis-h" aria-hidden="true"></i> More
      </button>
    </div>
    <div class="list-action-menu" [id]="menuId" [hidden]="!expanded">
      <ng-content></ng-content>
    </div>
  `,
  styles: [`
    :host { display: block; min-width: 0; }
    .list-action-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
    .list-more-button { margin: 0; min-height: 36px; padding: 6px 8px; font-size: .8rem; }
    .list-more-button[hidden] { display: none; }
    :host ::ng-deep .list-action-bar > :not(.list-more-button) { flex: 0 1 auto; min-width: 0; width: auto; margin: 0; }
    .list-action-menu { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; padding-top: 10px; margin-top: 10px; border-top: 1px solid var(--church-border, #dce3ea); }
    .list-action-menu[hidden] { display: none; }
  `]
})
export class ListActionsComponent implements AfterContentInit, OnDestroy {
  private static nextId = 0;
  private static active: ListActionsComponent | null = null;
  readonly menuId = `list-actions-${++ListActionsComponent.nextId}`;
  expanded = false;
  hasOverflow = false;
  private observer: MutationObserver;
  private order = new WeakMap<HTMLElement, number>();
  private nextOrder = 0;

  constructor(private element: ElementRef<HTMLElement>, private changes: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    this.arrangeActions();
    this.observer = new MutationObserver(() => this.arrangeActions());
    this.observer.observe(this.element.nativeElement, { childList: true, subtree: true });
  }

  private arrangeActions(): void {
    const host = this.element.nativeElement;
    const bar = host.querySelector<HTMLElement>('.list-action-bar');
    const menu = host.querySelector<HTMLElement>('.list-action-menu');
    const more = host.querySelector<HTMLElement>('.list-more-button');
    if (!bar || !menu || !more) return;
    const actions = [...Array.from(bar.children), ...Array.from(menu.children)].filter((node): node is HTMLElement =>
      node instanceof HTMLElement && node !== more &&
      (node.matches('button,a') || !!node.querySelector('button,a')));
    actions.forEach(action => { if (!this.order.has(action)) this.order.set(action, this.nextOrder++); });
    actions.sort((a,b) => Number(b.hasAttribute('listPrimaryAction')) - Number(a.hasAttribute('listPrimaryAction')) ||
      (this.order.get(a) - this.order.get(b)));
    // Prefer a useful secondary action over a destructive action beside Edit/Save.
    if (actions.length > 3 && actions[1] && /delete|restore|remove/i.test(actions[1].textContent || '')) {
      const safe = actions.findIndex((action,index) => index > 1 && !/delete|restore|remove/i.test(action.textContent || ''));
      if (safe > 1) actions.splice(1,0,actions.splice(safe,1)[0]);
    }
    const inline = inlineActionCount(actions.length);
    actions.forEach((action,index) => {
      const destination = index < inline ? bar : menu;
      if (action.parentElement !== destination) {
        if (destination === bar) bar.insertBefore(action, more); else menu.appendChild(action);
      }
    });
    this.hasOverflow = actions.length > 3;
    if (!this.hasOverflow) this.close();
    this.changes.detectChanges();
  }

  toggle(event: Event): void {
    event.stopPropagation();
    const open = !this.expanded;
    if (ListActionsComponent.active) ListActionsComponent.active.expanded = false;
    this.expanded = open;
    ListActionsComponent.active = open ? this : null;
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: Event): void {
    if (!this.element.nativeElement.contains(event.target as Node)) this.close();
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (!this.expanded) return;
    event.stopPropagation();
    this.close();
    this.element.nativeElement.querySelector<HTMLButtonElement>('.list-more-button')?.focus();
  }

  private close(): void {
    this.expanded = false;
    if (ListActionsComponent.active === this) ListActionsComponent.active = null;
  }

  ngOnDestroy(): void { this.observer?.disconnect(); this.close(); }
}
